<template>
  <!-- the one page header for every in-shell page. renders the horizon header
       rail under horizon/midnight and the legacy #eee dotted banner under
       classic, from the same props -- so converting a page is a template swap
       that leaves classic byte-equivalent and carries zero customer risk while
       the theme picker is still staff-gated.

       the rail half is the shipped treatment ops_desk / ops_board / vsm_board /
       departure_board / site_map / alerts already use: sunken-gradient band, 3px
       accent left edge, 22px/700 title, favorite star, right-aligned actions.

       deliberately no section eyebrow: the section is already the selected item
       in the sidebar, so naming it again here only costs vertical space. -->
  <div v-if="horizonNavActive" class="fi-pr-rail">
    <div class="fi-pr-text">
      <h3 class="fi-pr-title">
        {{ title }}
        <FiFavStar v-if="!noFav" />
        <span v-if="chip" class="fi-pr-chip">{{ chip }}</span>
      </h3>
      <div v-if="subtitle" class="fi-pr-sub">{{ subtitle }}</div>
      <div v-if="$slots.sub" class="fi-pr-sub"><slot name="sub" /></div>
    </div>
    <div v-if="$slots.actions" class="fi-pr-actions">
      <slot name="actions" />
    </div>
  </div>

  <!-- classic: the idiom that heads ~100 pages today, reproduced exactly
       (26px bold title, <br/>, 18px italic subtitle, #eee on 1px dotted grey,
       25px bottom margin). actions sit inline after the title the way the
       hand-rolled versions place them. -->
  <div v-else class="fi-pr-legacy" :class="{ 'fi-pr-legacy--compact': compact }">
    <span class="fi-pr-legacy-title">{{ title }}</span>
    <span v-if="$slots.actions" class="fi-pr-legacy-actions"><slot name="actions" /></span>
    <br />
    <span v-if="subtitle" class="fi-pr-legacy-sub">{{ subtitle }}</span>
    <span v-if="$slots.sub" class="fi-pr-legacy-sub"><slot name="sub" /></span>
  </div>
</template>

<script>
import horizonNav from '@/nav/horizon_nav_mixin'

export default {
  name: 'fi_page_rail',
  mixins: [horizonNav],
  props: {
    // the page name. required: this is the only place most pages say what they are.
    title: { type: String, required: true },
    // the italic line under the title in classic, the quiet line under the rail
    // title in horizon. optional.
    subtitle: { type: String, default: '' },
    // admin tenant-context chip, matching .od-rail-tenant-chip's role
    chip: { type: String, default: '' },
    // opt out of the favorite star (pages that are not nav destinations get no
    // star anyway -- FiFavStar self-gates -- so this is for deliberate cases)
    noFav: { type: Boolean, default: false },
    // the settings tabs use a tighter legacy banner than the rest of the app
    // (16px title, 3px padding, 15px bottom margin instead of 26px/25px). this
    // keeps classic byte-equivalent for them; the horizon rail is unaffected,
    // since there is no reason for settings to have a smaller rail than
    // everything else.
    compact: { type: Boolean, default: false },
  },
}
</script>

<style scoped>
/* ---- horizon / midnight: the house rail ---- */
.fi-pr-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.fi-pr-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1 1 auto;
  /* several of the pages this drops into center their whole body (the legacy
     banners were centered), and the rail is always left-aligned */
  text-align: left;
}

.fi-pr-title {
  margin: 0;
  padding: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--fi-text);
  letter-spacing: -0.1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fi-pr-sub {
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--fi-text-muted);
  margin-top: 2px;
}

/* admin tenant-context chip: the tenant whose data the page is rendering when
   it differs from the navbar selection. same treatment as .od-rail-tenant-chip */
.fi-pr-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid var(--fi-line);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: var(--fi-ops-eyebrow);
  white-space: nowrap;
}

.fi-pr-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* the rail bleeds into the content pane's gutters so its accent edge lines up
   with the page body below it, matching ops_board / site_map */
@media (max-width: 600px) {
  .fi-pr-rail {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .fi-pr-title { font-size: 19px; }
  .fi-pr-actions { width: 100%; }
}

/* ---- classic: the legacy banner, unchanged ---- */
.fi-pr-legacy {
  width: 100%;
  background-color: #eee;
  border: 1px dotted grey;
  margin-bottom: 25px;
}

.fi-pr-legacy-title {
  font-size: 26px;
  font-weight: bold;
}

.fi-pr-legacy-sub {
  font-style: italic;
  font-size: 18px;
}

.fi-pr-legacy-actions {
  float: right;
  margin: 4px 8px 0 0;
}

/* the settings-tab variant, matching tenant_config_shell's .tc-page-banner */
.fi-pr-legacy--compact {
  padding: 3px 10px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: bold;
}

.fi-pr-legacy--compact .fi-pr-legacy-title {
  font-size: 16px;
}
</style>
